import React, { PureComponent } from 'react';
import { Button, Popover, Tooltip, Popconfirm, Icon, message, BackTop } from 'antd';
import { services } from '@comall-backend-builder/core';
import { Emitter, PageManager } from '@comall-backend-builder/design-core';
import { ComponentSelector } from '../tools';
import { LinkTypeMode } from '../../types';

const L = services.language.getText;
const defaultProfile = {
    metainfo: {
        title: '',
        default: false,
        type: 'topic',
        activePeriod: {
            start: 'YYYY-MM-DD HH:mm:ss',
            end: 'YYYY-MM-DD HH:mm:ss',
        },
    },
    components: {},
    root: {
        showMiniCart: true,
        showBackToTop: false,
        backgroundColor: '#f9f9f9',
        share: {
            title: '',
            description: '',
            pic: '',
        },
        items: [],
    },
};

const pickKeysFromObject = (obj, ...keys) => {
    if (!obj || Object.keys(obj).length === 0) return {};
    const newObj = {};

    for (const key of keys) {
        if (key in obj) {
            newObj[key] = obj[key];
        }
    }

    return newObj;
};

interface IDesignPageProps {
    urlId: string;
    sourceRoot?: { image?: string; video?: string };
    client?: string;
    clientUrl?: (profile: any, fixed: boolean) => string;
    componentsSelector?: (props: { onSelect: (name: string) => void }) => JSX.Element;
    verbose: boolean;
    submit?: boolean | Record<string, any>;
    update?: boolean | Record<string, any>;
    cancel?: boolean | Record<string, any>;
    loadProfile: () => Promise<any>;
    linkTypes: Array<any>;
    linkTypeMode?: LinkTypeMode;
    uploadAction: string;
    isTemplate: boolean;
}

interface IDesignPageState {}

/*
 * 可视化配置页面
 */
export class DesignPage extends PureComponent<IDesignPageProps, IDesignPageState> {
    static defaultProps = {
        verbose: false,
        isTemplate: false,
        linkTypeMode: 'select',
    };

    clientReady = false;

    configData = null;

    state = {
        profile: null,
        structureFixed: false,
        iframeHeight: 667,
        configForm: null,
        componentAddConfirm: {
            show: false,
            top: 0,
            index: 0,
        },
        componentDeleteConfirm: {
            show: false,
            top: 0,
            index: 0,
        },
        resData: {},
    };

    pageManager;

    emitter;

    iframeRef;
    deleteLinkRef;

    initManager = () => {
        const { client, clientUrl, verbose } = this.props;
        this.pageManager = new PageManager();
        this.pageManager.addEventListener('profileChange', this.profileChange);
        let origin = client;
        // 本地开发使用
        // let origin = 'http://localhost:10086';
        if (clientUrl) {
            origin = clientUrl(null, true);
        }
        this.emitter = new Emitter({
            origin: origin,
            verbose: verbose,
        });

        this.emitter.on('client.ready', this.handleClientReady);
        this.emitter.on('component.selected', this.focusConfigForm);
        this.emitter.on('component.reorder', (payload) => {
            this.pageManager.reorder(payload.from, payload.to);
        });
        this.emitter.on('component.delete.confirm', this.handleDeleteConfirm);
        this.emitter.on('component.add.confirm', this.handleAddConfirm);
        this.emitter.on('page.resize', this.handlePageResize);

        this.loadData().then((data) => {
            let profile, structureFixed;
            this.configData = data;
            if (data && data.config) {
                profile = JSON.parse(data.config) || {};
                profile.metainfo.type = data.type;
                profile.metainfo.title = data.name;
                structureFixed = data.structureFixed || false;
            } else {
                profile = defaultProfile;
            }
            this.pageManager.update(profile);
            this.profileChange(profile);
            this.setState({ structureFixed: structureFixed, resData: data });
        });
    };

    /** iframe 加载完成，下发 profile */
    onIframeLoad = () => {
        this.emitter.setOptions({ messageWindow: this.iframeRef.contentWindow });
    };

    componentDidMount() {
        this.initManager();
    }

    handleClientReady = () => {
        this.clientReady = true;
        const { profile } = this.state;
        this.emitter.emit('page.render', profile);
    };

    handlePageResize = (payload) => {
        const { height } = payload;
        const iframeHeight = height < 667 ? 667 : height;
        this.setState({ iframeHeight: iframeHeight + 100 });
    };

    handleDeleteConfirm = (payload) => {
        const { top, index } = payload;
        this.setState(
            {
                componentDeleteConfirm: {
                    show: true,
                    top: top,
                    index: index,
                },
            },
            () => {
                this.deleteLinkRef.buttonNode.click();
            }
        );
    };

    handleAddConfirm = (payload: any) => {
        console.log('---->>>handleAddConfirm------------->>');
        const { resData } = this.state as any;
        const { top, index } = payload;
        // 是否从每日签到页面进入 是的话就不需要弹出待选组件panel
        const isSignIn = resData?.type === 'signIn';
        const showSignInComponent = () => {
            isSignIn && this.addComponent('productCard');
        };

        this.setState(
            {
                configForm: null,
                componentDeleteConfirm: { show: false },
                componentAddConfirm: { show: !isSignIn, top, index },
            },
            showSignInComponent
        );
    };

    addComponent = (name: string) => {
        this.setState({ componentAddConfirm: { show: false } });
        const index = this.state.componentAddConfirm.index;
        this.pageManager.add(name, index);
        setTimeout(() => this.emitter.emit('component.proceedSelect', { index }), 200);
    };

    deleteComponent = () => {
        const {
            componentDeleteConfirm: { index },
            configForm,
        } = this.state;
        this.pageManager.remove(index);
        if (configForm && configForm.index === index) {
            this.setState({ configForm: { form: null } });
        }
    };

    profileChange = (profile) => {
        this.setState({ profile });
        if (this.clientReady) {
            this.emitter.emit('page.render', profile);
        }
    };

    loadData = () => {
        const { loadProfile } = this.props;
        return loadProfile();
    };

    onSave = () => {
        const { submit } = this.props;
        const profile = this.pageManager.getProfile();
        const {
            root: { items },
        } = profile;
        let validate = true;
        for (let i = 0; i < items.length; i++) {
            const validateResult = this.pageManager.validate(i);
            if (!validateResult) {
                this.emitter.emit('component.proceedSelect', { index: i });
                message.error('请将必填项填写完整！');
                // setTimeout(() => {
                // const form = this.getConfigForm().form;
                // console.log('form ref', form._self.formRef.validateFields());
                // }, 200);
                validate = false;
                break;
            }
        }
        if (validate) {
            this.configData.config = JSON.stringify(profile);
            if (typeof submit === 'object' && submit.onClick) {
                submit.onClick(this.configData);
            }
        }
    };
    onUpdate = () => {
        const { update } = this.props;
        const profile = this.pageManager.getProfile();
        const {
            root: { items },
        } = profile;
        let validate = true;
        for (let i = 0; i < items.length; i++) {
            const validateResult = this.pageManager.validate(i);
            if (!validateResult) {
                this.emitter.emit('component.proceedSelect', { index: i });
                // setTimeout(() => {
                // const form = this.getConfigForm().form;
                // console.log('form ref', form._self.formRef.validateFields());
                // }, 200);
                validate = false;
                break;
            }
        }
        if (validate) {
            this.configData.config = JSON.stringify(profile);
            if (typeof update === 'object' && update.onClick) {
                update.onClick(this.configData);
            }
        }
    };
    onSetting = () => {
        if (!this.pageManager) {
            return;
        }
        this.setState({
            configForm: { index: -1 },
            componentDeleteConfirm: { show: false },
            componentAddConfirm: { show: false },
        });
    };

    setRef = (ref) => {
        if (!ref) {
            return;
        }
        this.iframeRef = ref;
        if (this.emitter) {
            this.emitter.setOptions({ messageWindow: this.iframeRef.contentWindow });
            setTimeout(() => {
                if (this.clientReady) {
                    this.emitter.emit('page.render', this.state.profile);
                }
            }, 100);
        }
    };

    closeConfigForm = () => {
        this.setState({ configForm: { form: null } });
    };

    focusConfigForm = (payload) => {
        const { index, top } = payload;
        const { configForm } = this.state;

        if (!this.pageManager) {
            return;
        }
        if (configForm && configForm.top === top && configForm.index === index) {
            return;
        }
        this.setState({
            configForm: { top, index },
            componentDeleteConfirm: { show: false },
            componentAddConfirm: { show: false },
        });
    };

    getConfigForm() {
        const { linkTypes, linkTypeMode, uploadAction, sourceRoot } = this.props;
        const { configForm } = this.state;
        if (!this.pageManager) {
            return null;
        }
        if (!configForm || configForm.index === undefined) {
            return null;
        }
        const { index, top } = configForm;
        if (index === -1) {
            let profile = this.pageManager.getProfile();
            let c = this.pageManager.getRootConfigForm();
            const { metainfo } = profile;
            let configFormTitle = L('components.PageDesign.pageSetting');
            let form = c.render({ sourceRoot, uploadAction, ...metainfo });
            return { form, top: 10, left: 20, title: configFormTitle, index: -1 };
        }
        let c = this.pageManager.getConfigForm(index);
        let configFormTitle = c.registerConfig.title;
        let form = c.render({ linkTypes, linkTypeMode, sourceRoot, uploadAction });
        return { form, top, left: 0, title: configFormTitle, index };
    }

    renderCancel = () => {
        const { cancel } = this.props;
        const goBack = L('common.goBack');
        const defaultProps = {
            type: 'default',
            style: { marginLeft: 20 },
            onClick: () => {},
        };
        let props;
        if (cancel === false) {
            return null;
        }
        if (typeof cancel === 'object') {
            props = Object.assign({}, defaultProps, cancel);
        }
        return <Button {...props}>{goBack}</Button>;
    };

    renderSubmit = () => {
        const { submit } = this.props;
        const save = L('common.save');
        const defaultProps = {
            type: 'primary',
            onClick: () => {},
        };
        let props;
        if (submit === false) {
            return null;
        }
        if (typeof submit === 'object') {
            props = Object.assign({}, defaultProps, submit, { onClick: this.onSave });
        }
        return <Button {...props}>{save}</Button>;
    };
    renderUpdate = () => {
        const { update } = this.props;
        const save = '更新积分商品';
        const defaultProps = {
            type: 'primary',
            style: { marginLeft: 20 },
            onClick: () => {},
        };
        let props;
        if (update === false) {
            return null;
        }
        if (typeof update === 'object') {
            props = Object.assign({}, defaultProps, update, { onClick: this.onUpdate });
        }
        return <Button {...props}>{save}</Button>;
    };
    getClientUrl = (profile: any) => {
        const { client, isTemplate, clientUrl } = this.props;
        console.log('---frj----props: ', this.props);
        const { structureFixed, resData } = this.state;
        if (clientUrl) {
            return clientUrl(profile, structureFixed);
        }

        const newIframeData = encodeURIComponent(
            JSON.stringify(pickKeysFromObject(resData || {}, ...['margin', 'bgcolor', 'id']))
        );
        // version 为了阻止H5缓存，每次改变版本号
        let url = `${client}?page=WYSIWG&mode=edit&configData=${newIframeData}&version=1.0.0`;
        // 本地开发使用 http://localhost:10086是本地起h5的地址
        // let url = `http://localhost:10086?page=WYSIWG&mode=edit&configData=${newIframeData}`;
        if (profile) {
            url = url + '&type=' + profile.metainfo.type;
            if (!isTemplate) {
                url = url + '&fixed=' + structureFixed;
            }
        }
        return url + '&v0.1';
    };
    // 此生命周期可以作为 react 在 prop 传入之后， render() 渲染之前更新 state 的机会。
    // 组件初次渲染时不会执行componentWillReceiveProps；当props发生变化时执行componentWillReceiveProps
    // 通过调用this.setState()来更新组件状态渲染页面（此生命周期可以解决父组件props变化，子组件页面不更新问题）
    UNSAFE_componentWillReceiveProps(
        nextProps: Readonly<IDesignPageProps>,
        nextContext: any
    ): void {
        console.log('会触发这个嘛');
        this.loadData().then((data) => {
            let profile, structureFixed;
            this.configData = data;
            if (data && data.config) {
                profile = JSON.parse(data.config) || {};
                profile.metainfo.type = data.type;
                profile.metainfo.title = data.name;
                structureFixed = data.structureFixed || false;
            } else {
                profile = defaultProfile;
            }
            this.pageManager.update(profile);
            this.profileChange(profile);
            this.setState({ structureFixed: structureFixed, resData: data });
        });
    }
    render() {
        const { componentsSelector } = this.props;
        console.log('---->>>', componentsSelector);
        const { profile, componentDeleteConfirm, componentAddConfirm, iframeHeight } = this.state;
        console.log('---frj----state: ', this.state);
        const configForm = this.getConfigForm();
        const url = this.getClientUrl(profile);

        return (
            <div className="design-page">
                {profile && (
                    <div style={{ height: iframeHeight, border: '2px solid #ccc' }}>
                        <iframe
                            key={url}
                            style={{ height: iframeHeight, border: 'none' }}
                            ref={this.setRef}
                            src={url}
                            title="design"
                            className="iframe-design"
                            onLoad={this.onIframeLoad}
                        />
                    </div>
                )}
                <div className="design-config-panel">
                    <div className="design-actions">
                        {this.renderSubmit()}
                        {this.renderCancel()}
                        {profile &&
                            profile.metainfo &&
                            profile.metainfo.type &&
                            profile.metainfo.type == 'point' &&
                            this.renderUpdate()}
                    </div>

                    {/* 页面设置按钮 */}
                    <Tooltip title={L('components.PageDesign.settingInformation')}>
                        <Icon type="setting" className="page-setting" onClick={this.onSetting} />
                    </Tooltip>

                    {/* 删除确认弹窗 */}
                    {componentDeleteConfirm.show && (
                        <div className="delete-confirm" style={{ top: componentDeleteConfirm.top }}>
                            <Popconfirm
                                title={L('components.PageDesign.removeConfirm')}
                                placement="bottomRight"
                                onConfirm={this.deleteComponent}
                                okText={L('common.ok')}
                                cancelText={L('common.cancel')}
                            >
                                <Button
                                    type={'link'}
                                    ref={(ref) => (this.deleteLinkRef = ref)}
                                ></Button>
                            </Popconfirm>
                        </div>
                    )}

                    {/* 配置表单 panel */}
                    {configForm && (
                        <div
                            key={'config-form-' + configForm.index}
                            className="config-form"
                            style={{ top: configForm.top - 60, left: configForm.left }}
                        >
                            <Popover
                                content={
                                    <div className="config-form-content" style={{ minHeight: 300 }}>
                                        {configForm.form}
                                    </div>
                                }
                                title={
                                    <div className="config-form-title">
                                        {configForm.title}
                                        <a
                                            className="config-form-close"
                                            onClick={this.closeConfigForm}
                                        >
                                            <Icon type="close" />
                                        </a>
                                    </div>
                                }
                                visible={true}
                                overlayStyle={{ minWidth: 600, minHeight: 300 }}
                                placement="rightTop"
                            />
                        </div>
                    )}

                    {/* 待选组件 panel */}
                    {componentAddConfirm.show && (
                        <div className="add-confirm" style={{ top: componentAddConfirm.top - 20 }}>
                            <Popover
                                content={
                                    <div style={{ minHeight: 300 }}>
                                        {componentsSelector ? (
                                            componentsSelector({ onSelect: this.addComponent })
                                        ) : (
                                            <ComponentSelector onSelect={this.addComponent} />
                                        )}
                                    </div>
                                }
                                // title={L('components.PageDesign.addComponent')}
                                title={'选择组件'}
                                visible={true}
                                overlayClassName="design-component-selector-panel"
                                placement="rightTop"
                            />
                        </div>
                    )}
                </div>
                <BackTop />
            </div>
        );
    }
}
